// Cover images are embedded directly in the saved post, so they need to stay
// small. A raw phone photo can be 5-10MB as base64, which is what caused the
// old localStorage saves to fail silently. This resizes and recompresses the
// image in the browser before it is stored.

const MAX_DATA_URL_CHARS = 1_200_000; // roughly 900KB of actual image data

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the image'));
    img.src = src;
  });
}

function renderJpeg(img: HTMLImageElement, targetWidth: number, quality: number): string {
  const width = Math.max(1, Math.min(targetWidth, img.naturalWidth || img.width));
  const scale = width / (img.naturalWidth || img.width);
  const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

export async function compressImageToDataUrl(file: File): Promise<string> {
  const original = await readAsDataUrl(file);

  // Animations and non-image files are left untouched.
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return original;
  }

  try {
    const img = await loadImage(original);
    const widths = [1600, 1280, 1000, 820, 640];
    const qualities = [0.82, 0.72, 0.62, 0.5];
    let smallest = original.length <= MAX_DATA_URL_CHARS ? original : '';

    for (const width of widths) {
      for (const quality of qualities) {
        const candidate = renderJpeg(img, width, quality);
        if (!candidate) continue;
        if (!smallest || candidate.length < smallest.length) smallest = candidate;
        if (candidate.length <= MAX_DATA_URL_CHARS) return candidate;
      }
    }

    return smallest || original;
  } catch {
    return original;
  }
}